import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// ES2022+: Static configuration moved outside component
const DISPLAY_VALUE_MAP = {
  cv: 'Bulk + Absorb',
  postCv: 'Absorb 2', 
  float: 'Float',
}

const INITIAL_STATUS = {
  connected: false,
  controlled: false,
  cutoffSignal: false,
  cutoffBatteries: [], // Array of battery numbers (1, 2) that are cut off
  cutoffReasons: {}, // Object mapping battery numbers to arrays of reason strings
  engineOff: false, // Any alternator has RPM = 0
  engineOffAlternators: [], // Array of alternator numbers (1, 2) with RPM = 0
  ignitionOff: false, // Any alternator has ignition OFF
  ignitionOffAlternators: [], // Array of alternator numbers (1, 2) with ignition OFF
  chargeStage: null,
  targetVolts: null,
  targetAmps: null,
  alt1TargetAmps: null,
  alt2TargetAmps: null,
  alt1BoostEngaged: false,
  alt2BoostEngaged: false,
}

export const useCanBusStatus = (realTimeData = []) => {
  const [status, setStatus] = useState(INITIAL_STATUS)
  // Timeout window for switching modes
  const MODE_TIMEOUT_MS = 15000 // 15 seconds
  // Track last-seen times for connection/controlled signals
  const lastSeenRef = useRef({
    connected: 0,
    controlled: 0,
  })
  // React 19: Simplified helper functions with better performance
  const getLatestValidValue = useCallback((data, device, measurement) => {
    if (!Array.isArray(data)) return null
    
    const deviceData = data.filter(item => 
      item.device === device && item.measurement === measurement,
    )
    
    if (!deviceData.length) return null
    
    // ES2022+: Simplified sorting and finding
    const sorted = deviceData.sort((a, b) => 
      new Date(b._time || b.time || 0) - new Date(a._time || a.time || 0),
    )
    
    return sorted.find(item => item._value != null)?._value ?? null
  }, [])

  // ES2022+: Simplified control detection
  const hasDesiredValues = useCallback((data) => 
    data.some(item => 
      ['desiredVolts', 'desiredAmps'].includes(item.measurement) && 
      item._value != null,
    ), [])
  // React 19: Streamlined status processing
  const processCanBusStatus = useCallback(() => {
    const now = Date.now()
    const newStatus = { ...INITIAL_STATUS }
    
    // Check CAN bus connection (same logic), but apply 15s timeout window
    const presentConnectedNow = ['bat1', 'bat2'].some(device => 
      getLatestValidValue(realTimeData, device, 'canbusConnected') != null,
    )
    if (presentConnectedNow) {
      lastSeenRef.current.connected = now
    }
    newStatus.connected = (now - lastSeenRef.current.connected) <= MODE_TIMEOUT_MS
    
    // ES2022+: Process measurements with simplified logic
    // Track which batteries are cut off, their reasons, and which alternators have no RPM/ignition
    const cutoffBatteries = []
    const cutoffReasons = {}
    const engineOffAlternators = []
    const ignitionOffAlternators = []
    
    // Reason text mapping
    const reasonMap = {
      analogCutoff: 'Analog cutoff',
      canbusCutoff: 'CAN bus cutoff',
      canTimeoutCutoff: 'CAN bus required but not connected',
      globalCanbusCutoff: 'Charging disabled by RV-C command',
    }
    
    for (const item of realTimeData) {
      const { measurement, device, _value } = item
      if (_value == null) continue
      switch (measurement) {
        case 'chargeStage':
          newStatus.chargeStage = _value
          break
        case 'desiredVolts':
          if (device?.startsWith('bat')) newStatus.targetVolts = _value
          break
        case 'desiredAmps':
          if (device?.startsWith('bat')) newStatus.targetAmps = _value
          break
        case 'combinedChargeCutoff':
          if (_value !== 0 && device?.startsWith('bat')) {
            const batNum = device === 'bat1' ? 1 : device === 'bat2' ? 2 : null
            if (batNum && !cutoffBatteries.includes(batNum)) cutoffBatteries.push(batNum)
          }
          break
        case 'analogCutoff':
        case 'canbusCutoff':
        case 'canTimeoutCutoff':
        case 'globalCanbusCutoff':
          if (_value !== 0 && device?.startsWith('bat')) {
            const batNum = device === 'bat1' ? 1 : device === 'bat2' ? 2 : null
            if (batNum) {
              if (!cutoffReasons[batNum]) cutoffReasons[batNum] = []
              const reasonText = reasonMap[measurement]
              if (!cutoffReasons[batNum].includes(reasonText)) {
                cutoffReasons[batNum].push(reasonText)
              }
            }
          }
          break
        case 'motorRpm':
          if (device === 'alt1' || device === 'alt2') {
            const altNum = device === 'alt1' ? 1 : 2
            if (parseFloat(_value) === 0 && !engineOffAlternators.includes(altNum)) {
              engineOffAlternators.push(altNum)
            }
          }
          break
        case 'ignitionOn':
          if (device === 'alt1' || device === 'alt2') {
            const altNum = device === 'alt1' ? 1 : 2
            // Value comes as string from InfluxDB: "true", "false", or "null"
            // Only add to ignitionOffAlternators if explicitly "false" (not "null"/"true")
            if (_value === 'false' && !ignitionOffAlternators.includes(altNum)) {
              ignitionOffAlternators.push(altNum)
            }
          }
          break
        case 'boostEngaged': {
          const isEngaged = _value === true || _value === 'true'
          if (device === 'alt1') newStatus.alt1BoostEngaged = isEngaged
          else if (device === 'alt2') newStatus.alt2BoostEngaged = isEngaged
          break
        }
      }
    }
    newStatus.cutoffSignal = cutoffBatteries.length > 0
    newStatus.cutoffBatteries = cutoffBatteries
    newStatus.cutoffReasons = cutoffReasons
    newStatus.engineOff = engineOffAlternators.length > 0
    newStatus.engineOffAlternators = engineOffAlternators
    newStatus.ignitionOff = ignitionOffAlternators.length > 0
    newStatus.ignitionOffAlternators = ignitionOffAlternators
    
    // Get alternator target amps
    newStatus.alt1TargetAmps = getLatestValidValue(realTimeData, 'alt1', 'desiredAmps')
    newStatus.alt2TargetAmps = getLatestValidValue(realTimeData, 'alt2', 'desiredAmps')
    // Determine controlled state (same logic), but apply 15s timeout window
    const presentControlledNow = hasDesiredValues(realTimeData)
    if (presentControlledNow) {
      lastSeenRef.current.controlled = now
    }
    newStatus.controlled = (now - lastSeenRef.current.controlled) <= MODE_TIMEOUT_MS
    
    setStatus(newStatus)
  }, [realTimeData, getLatestValidValue, hasDesiredValues])
  useEffect(() => {
    processCanBusStatus()
  }, [processCanBusStatus])

  // ES2022+: Simplified computed values
  const connectionMode = useMemo(() => {
    if (status.connected) return status.controlled ? 'controlled' : 'connected'
    return 'direct'
  }, [status.connected, status.controlled])

  const translatedChargeStage = useMemo(() => {
    if (!status.chargeStage) return null
    return DISPLAY_VALUE_MAP[status.chargeStage] || status.chargeStage
  }, [status.chargeStage])

  return {
    status,
    connectionMode,
    translatedChargeStage,
    displayValueMap: DISPLAY_VALUE_MAP,
    refresh: processCanBusStatus,
  }
}
